import { useContext, useEffect, useState } from "react";

import { DASH_LINKS } from "../../const/AppCardLinks";
import { formatDate, getEpoch } from "../DateFormatter";
import {
    getActionControl,
    isAllowed,
    parentRequestBetweenRounds,
    parentViewBetweenRounds
} from "../ActionControlWindows";
import { GlobalContext } from "../../components/contextProvider/ContextProvider";

/**
 * OPEN ENROLLMENT BUSINESS RULES
 * ------------------------------------------------------------------------------------------------
 *	Student is NOT GradeLevelType: THIRTEENTH
 *	ROUND1 OR ROUND2 must be open
 *	get OeStudentInfo
 *	——main content——
 *	get DistricWindows for Current Year (user/active/schoolyear)
 *	if no schoolChoices
 *		if parentRequestBetweenRounds
 *			getParentRequestRoundTwo
 *			$main = Round 1 closed. Please come back for Round 2 starting $parentRequestRound2.startDate
 *	if OeStudentInfo AND (parentRequestAllowed OR parentOfferAllowed OR schoolOfferAllowed OR parentViewBetweenRounds)
 *		if schoolChoices
 *			$main = Manage School Choices
 *		else
 *			if parentRequestAllowed
 *				$main = Select School Choices
 *	else if parentRequestAllowed
 *		$main = Start Open Enrollment
 *	——subtitle ——-
 *	if OeStudentInfo
 *		get SchoolChoices
 *		if parentRequestAllowed
 *			if studentNumber
 *				$subtitle = View Student Info
 *			else
 *				$subtitle = Edit Student Info
 *	——school choice title ——
 *	if parentRequestAllowed
 *		$title = Manage School Choices
 *	if Round1
 *		if parentOfferAllowed
 *			$title = View School Offers
 *		if schoolOfferAllowed
 *			$title = Schools are Making Offers
 *	if parentViewBetweenRoundOneAndTwo
 *		$title = View School Offer Status
 *	——new student cards——
 *	Round1 OR Round2 must be open
 *	$OeStudents = getStudentsByParent(openEnrollment/student/byParent/{$userDetails.uid}/{$districtWindow.schoolYearKey}/index.json)
 *	if $OeStudents
 *		forEach $OeStudents as $student where $student.studentNumber = null
 *			return a card including: firstName, middleName, lastName, gradeName, address,
 *	if parentRequestAllowed
 *		add student button allowed
 *
 *	Status = “0 School Choices Made” or “2 school choices made”, “Action Required” (if parent needs to accept offerchoose)
 *	More Info includes any school choices
 *
 *	main button text is contextual like “Manage School Choices”, “View School Offers”
 * Only show school choice more info if the following:
 * ROUND1 or ROUND2 is open
 * adminOfferConfirmAllowed or parentOfferAllowed or parentRequestAllowed or schoolFinalizeAllowed or schoolOfferPendingAllowed
 * studentNumber
 * schoolChoices
 *
 * ------------------------------------------------------------------------------------------------
 * Custom hook to determine if a student's guardian has access to the Open Enrollment app tile
 * @name useOeApp
 * @param {{}} student
 * @returns {{}|null} appObj
 */
export const useOeApp = (student) => {
    const { dispatch, state } = useContext(GlobalContext);
    const { oeActionControls, oeActiveDistrictWindow, oeDistrictWindows, oeSchoolYearKey, studentAppObjects } =
        state || {};

    const [appObj, setAppObj] = useState(null);
    const [isAdminOfferConfirmAllowed, setIsAdminOfferConfirmAllowed] = useState(null);
    const [isParentRequestAllowed, setIsParentRequestAllowed] = useState(null);
    const [isParentOfferAllowed, setIsParentOfferAllowed] = useState(null);
    const [isSchoolFinalizeAllowed, setIsSchoolFinalizeAllowed] = useState(null);
    const [isSchoolOfferAllowed, setIsSchoolOfferAllowed] = useState(null);
    const [isSchoolOfferPendingAllowed, setIsSchoolOfferPendingAllowed] = useState(null);
    const [isParentViewBetweenRounds, setIsParentViewBetweenRounds] = useState(null);
    const [isParentRequestBetweenRoundsFlag, setIsParentRequestBetweenRoundsFlag] = useState(null);
    const [loading, setLoading] = useState(true);
    const [oeWindowFlag, setOeWindowFlag] = useState(false);
    const [parentRequest, setParentRequest] = useState(null);
    const [parentRequestRound2, setParentRequestRound2] = useState(null);
    /* eslint-disable-next-line */
    const [schoolOffer, setSchoolOffer] = useState(null);

    const studentKey = student?.key;
    const existing = studentAppObjects?.[studentKey]?.OPEN_ENROLLMENT || null;

    /**
     * If we have the student's apps in context, set the appObj for return
     */
    useEffect(() => {
        if (existing) {
            setAppObj(existing.appObj);
            setLoading(false);
        }
    }, [existing]);

    /**
     * Skip entirely if we've already built this app
     * Otherwise, set the available windows needed to determine if the app is relevant, and which actions the guardian
     * can take.
     */
    useEffect(() => {
        if (existing) {
            return;
        }
        if (!oeActionControls || !oeActiveDistrictWindow || !oeDistrictWindows || !oeSchoolYearKey) {
            setAppObj(null);
            setLoading(true);

            return;
        }

        setLoading(true);
        try {
            setIsAdminOfferConfirmAllowed(
                isAllowed("ADMIN_OFFER_CONFIRM", oeActionControls, oeActiveDistrictWindow, oeDistrictWindows)
            );
            setIsParentOfferAllowed(
                isAllowed("PARENT_OFFER", oeActionControls, oeActiveDistrictWindow, oeDistrictWindows)
            );
            setIsParentRequestAllowed(
                isAllowed("PARENT_REQUEST", oeActionControls, oeActiveDistrictWindow, oeDistrictWindows)
            );
            setIsSchoolFinalizeAllowed(
                isAllowed("SCHOOL_FINALIZE", oeActionControls, oeActiveDistrictWindow, oeDistrictWindows)
            );
            setIsSchoolOfferAllowed(
                isAllowed("SCHOOL_OFfER", oeActionControls, oeActiveDistrictWindow, oeDistrictWindows)
            );
            setIsSchoolOfferPendingAllowed(
                isAllowed("SCHOOL_OFFER_PENDING", oeActionControls, oeActiveDistrictWindow, oeDistrictWindows)
            );
            setIsParentRequestBetweenRoundsFlag(parentRequestBetweenRounds(oeActionControls, oeDistrictWindows));
            setIsParentViewBetweenRounds(parentViewBetweenRounds(oeActionControls, oeDistrictWindows));

            setParentRequest(getActionControl("PARENT_REQUEST", oeActionControls, oeActiveDistrictWindow));
            setParentRequestRound2(getActionControl("PARENT_REQUEST", oeActionControls, oeDistrictWindows[1]));
            setSchoolOffer(getActionControl("SCHOOL_OFFER", oeActionControls, oeActiveDistrictWindow));
            setOeWindowFlag(true);
        } catch (error) {
            console.error("Unable to set Open Enrollment permission windows:\n", error);
            setAppObj(null);
            setLoading(false);
        }
    }, [oeActionControls, oeActiveDistrictWindow, oeDistrictWindows, oeSchoolYearKey, existing]);

    /**
     * Skip entirely if we've already built this app
     * Otherwise, construct the Open Enrollment appObj and update statuses per student
     * Save the appObj into context
     */
    useEffect(() => {
        if (existing || !oeWindowFlag || !student) {
            setLoading(false);
            return;
        }

        try {
            const { schoolChoiceDetailDtos } = student;
            let status = null;
            let statusNumber = null;
            let statusColor = "#454545";

            if (schoolChoiceDetailDtos) {
                if (schoolChoiceDetailDtos.length === 1) {
                    // 1 School Choice Made
                    status = "OEApp_1School";
                    statusColor = "#3BC2E6"; // grey;
                } else if (schoolChoiceDetailDtos.length > 0) {
                    // ${schoolChoiceDetailDtos.length} School Choices Made
                    status = "OEApp_MoreSchools";
                    statusColor = "#0669B3"; // pearl-blue
                    statusNumber = schoolChoiceDetailDtos.length;
                } else {
                    // No School Choices Made
                    status = "OEApp_NoSchools";
                    statusColor = "#CB321A"; // red
                }
            }

            if (isParentRequestAllowed && !student.studentKey) {
                // Not Started
                status = "Global_NotStarted";
                statusColor = "#707070"; // grey3a
            }

            let oeApp = {
                app: "OPEN_ENROLLMENT",
                // Application process for parents to elect enrollment consideration for their student(s) at DCSD neighborhood and charter schools.
                body: "OEApp_Body",
                buttons: [
                    {
                        // Go to Open Enrollment
                        label: `OEApp_Btn6`,
                        href: student.studentNumber
                            ? `${DASH_LINKS.open_enrollment}/view/${student.studentNumber}`
                            : `${DASH_LINKS.open_enrollment}/edit/${student?.key}`
                    }
                ],
                endDate: formatDate(parentRequest.endDate, true, true),
                icon: "OPEN_ENROLLMENT_ICON",
                moreInfo: {},
                startDate: formatDate(parentRequest.startDate, true, true),
                status,
                statusColor,
                statusNumber,
                subBody: null,
                subBodyRange: null,
                // Open Enrollment
                title: "OEApp_Header"
            };

            if ((!schoolChoiceDetailDtos || schoolChoiceDetailDtos.length < 1) && isParentRequestBetweenRoundsFlag) {
                // Round 1 closed. Please come back for Round 2 starting
                oeApp.subBody = "OEApp_RoundClosed";
                oeApp.subBodyRange = formatDate(getEpoch(parentRequestRound2.startDate), true, true);
            }

            if (
                student.studentKey &&
                (isParentRequestAllowed || isParentOfferAllowed || isSchoolOfferAllowed || isParentViewBetweenRounds)
            ) {
                oeApp.buttons[0].href = `${DASH_LINKS.open_enrollment}/school-choice/${student.studentKey}`;
                if (schoolChoiceDetailDtos?.length > 0) {
                    // Manage School Choices
                    oeApp.buttons[0].label = "OEApp_Btn2";
                } else if (isParentRequestAllowed) {
                    // Select School Choices
                    oeApp.buttons[0].label = "OEApp_Btn5";
                }

                if (student.studentNumber) {
                    // View Student Info
                    oeApp.buttons.push({
                        label: "OEApp_Btn3",
                        href: `${DASH_LINKS.open_enrollment}/view/${student.studentNumber}`
                    });
                } else {
                    // Edit Student Info
                    oeApp.buttons.push({
                        label: "OEApp_Btn4",
                        href: `${DASH_LINKS.open_enrollment}/edit/${student.studentKey}`
                    });
                }
            } else if (isParentRequestAllowed && !student.studentKey) {
                // Start Open Enrollment
                oeApp.buttons[0].label = "OEApp_Btn1";
                oeApp.buttons[0].href = `${DASH_LINKS.open_enrollment}/view/${student.studentNumber}`;
            }

            if (
                isAdminOfferConfirmAllowed ||
                isParentRequestAllowed ||
                isParentOfferAllowed ||
                isSchoolOfferPendingAllowed ||
                isSchoolFinalizeAllowed
            ) {
                if (schoolChoiceDetailDtos?.length > 0) {
                    oeApp.buttons.push({
                        // More Info
                        label: "Global_MoreInfo",
                        clickIt: true
                    });
                }
            }

            // ✅ Save to global context cache
            dispatch({
                type: "StudentAppObjects",
                studentAppObjects: {
                    ...studentAppObjects,
                    [studentKey]: {
                        ...studentAppObjects?.[studentKey],
                        ["OPEN_ENROLLMENT"]: {
                            appObj: oeApp
                        }
                    }
                }
            });
        } catch (error) {
            console.error("Unable to retrieve Open Enrollment App:\n", error);
            setAppObj(null);
        } finally {
            setLoading(false);
        }
    }, [
        dispatch,
        existing,
        isAdminOfferConfirmAllowed,
        isParentRequestAllowed,
        isParentOfferAllowed,
        isSchoolOfferAllowed,
        isParentViewBetweenRounds,
        isParentRequestBetweenRoundsFlag,
        isSchoolFinalizeAllowed,
        isSchoolOfferPendingAllowed,
        oeWindowFlag,
        parentRequest,
        parentRequestRound2,
        student,
        studentAppObjects,
        studentKey
    ]);

    return { appObj, loading };
};
